import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type {
  ChangePasswordRequest,
  ChangePasswordResponse,
  LoginRequest,
  PublicUser,
  RegisterRequest,
  SetWithdrawalPasswordRequest,
  UpdateProfileRequest,
} from '@/contracts/auth.contract'
import { authApi } from '@/services/auth.api'
import { connectAuthBridge } from '@/services/http/client'

/**
 * 認證狀態。
 *
 * `accessToken` **只存在記憶體** —— 不寫 localStorage、不寫 cookie。
 * 重新整理後會消失，靠 httpOnly cookie 裡的 refresh token 換回來（`restore()`）。
 * 這樣即使有 XSS，也偷不到任何可長期使用的憑證。
 *
 * 這是 T1 的反面：那邊把整個使用者物件（含 token 欄位）序列化進 localStorage。
 */
/**
 * 「這個瀏覽器登入過」的旗標。
 *
 * 它**不是憑證**，只是一個布林值 —— 真正的 refresh token 在 httpOnly cookie 裡，
 * JS 讀不到也寫不到，所以偽造這個旗標的唯一後果是白打一次 refresh 然後拿到 401。
 *
 * 存在的理由：access token 只在記憶體，重新整理後前端無從得知自己是不是登入狀態，
 * 唯一的辦法是拿 cookie 去問。但**從沒登入過的訪客問了必定得到 401** ——
 * 那筆 401 會進存取日誌、進 devtools、進 Sentry，看起來像壞掉但什麼事都沒有。
 * 有了這個旗標，訪客直接跳過那一趟，首屏也少一個往返。
 */
const SESSION_HINT_KEY = 'auth.hasSession'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<PublicUser | null>(null)
  const accessToken = ref<string | null>(null)
  /** 首次啟動時是否還在嘗試回復 session。守衛要等它結束才能判斷登入狀態 */
  const isRestoring = ref(true)

  const isAuthenticated = computed(() => user.value !== null && accessToken.value !== null)

  function setSession(session: { user: PublicUser; accessToken: string }): void {
    user.value = session.user
    accessToken.value = session.accessToken
    localStorage.setItem(SESSION_HINT_KEY, '1')
  }

  function clearSession(): void {
    user.value = null
    accessToken.value = null
    localStorage.removeItem(SESSION_HINT_KEY)
  }

  async function register(payload: RegisterRequest): Promise<void> {
    setSession(await authApi.register(payload))
  }

  async function login(payload: LoginRequest): Promise<void> {
    setSession(await authApi.login(payload))
  }

  /** 更新個人資料。後端回傳更新後的完整使用者，直接覆蓋 store */
  async function updateProfile(payload: UpdateProfileRequest): Promise<void> {
    const { user: updated } = await authApi.updateProfile(payload)
    user.value = updated
  }

  /**
   * 設定或修改取款密碼。
   *
   * 回傳的使用者只帶 `hasWithdrawalPassword` 布林值 ——
   * PIN 本身與其雜湊都不會離開後端。
   */
  async function setWithdrawalPassword(payload: SetWithdrawalPasswordRequest): Promise<void> {
    const { user: updated } = await authApi.setWithdrawalPassword(payload)
    user.value = updated
  }

  /**
   * 修改密碼。
   *
   * 不動 `accessToken` —— 後端保留了本機這條輪替鏈，所以目前的 session 仍然有效。
   * 回傳被踢掉的裝置數，讓畫面能明確告訴使用者發生了什麼。
   */
  function changePassword(payload: ChangePasswordRequest): Promise<ChangePasswordResponse> {
    return authApi.changePassword(payload)
  }

  async function logout(): Promise<void> {
    try {
      await authApi.logout()
    } finally {
      // 後端呼叫失敗也一定要清掉前端狀態，否則使用者會卡在「看起來已登入」
      clearSession()
    }
  }

  /**
   * 應用啟動時嘗試回復 session。
   *
   * 這個瀏覽器沒登入過就**完全不發請求**（見 `SESSION_HINT_KEY`）。
   * 有旗標才拿 cookie 去換，失敗時靜默清掉 —— 那只代表「目前是訪客」，
   * 不是錯誤，不該對使用者顯示任何提示。
   *
   * 旗標沒有到期時間，而 refresh cookie 有（30 天，且每次輪替會延長）。
   * 所以「登入過但很久沒回來」的人仍然會打出一次 401，然後旗標被清掉、
   * 下次就不打了。**這一次是省不掉的** —— cookie 讀不到，只能問後端。
   *
   * 用共用的 promise 確保只會執行一次：路由守衛可能在多次導航中重複呼叫，
   * 而 refresh 有輪替機制 —— 併發呼叫會讓後到的那次被判定為 token 重用。
   */
  let restorePromise: Promise<void> | null = null

  function restore(): Promise<void> {
    restorePromise ??= (
      localStorage.getItem(SESSION_HINT_KEY) === null
        ? Promise.resolve()
        : authApi.refresh().then(setSession).catch(clearSession)
    ).finally(() => {
      isRestoring.value = false
    })

    return restorePromise
  }

  return {
    user,
    accessToken,
    isRestoring,
    isAuthenticated,
    register,
    login,
    logout,
    restore,
    clearSession,
    updateProfile,
    changePassword,
    setWithdrawalPassword,
  }
})

/**
 * 把 store 接到 HTTP 層。
 *
 * 方向是刻意的：由 store 主動注入自己給 services，
 * 而不是 services 去 import store —— 後者會讓傳輸層依賴 pinia，
 * 破壞分層（見 ARCHITECTURE.md）。
 */
export function installAuthBridge(onSessionLost: () => void): void {
  const store = useAuthStore()

  connectAuthBridge({
    getAccessToken: () => store.accessToken,
    refresh: async () => {
      const session = await authApi.refresh()
      store.$patch({ user: session.user, accessToken: session.accessToken })
      return session.accessToken
    },
    onSessionLost: () => {
      store.clearSession()
      onSessionLost()
    },
  })
}
